/**
 * Profile picture: change or remove it.
 *
 * The flow is intentionally hybrid:
 *
 *   1. The binary data goes via REST (`POST /api/avatar`) — it does not fit in the
 *      sync queue, and the server has to normalize it (crop, WebP, no EXIF).
 *   2. The KEY it returns is written to the local row with Drizzle, just
 *      like the language, font, and theme. The data uploads through the write-path.
 *
 * Practical consequence: the picture appears immediately and remains after a
 * cold reload without a connection.
 */
import { msg } from "@lingui/core/macro";
import { type Accessor, createSignal, Show } from "solid-js";
import { Camera, Loader2, Trash2 } from "@fleet/icons";
import { updateUserImageMutation } from "@bso/shared";

import { Avatar } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { deleteAvatar, uploadAvatar } from "@/lib/api";
import { prepareAvatarFile, validateAvatarFile } from "@/lib/avatar-file";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useI18n } from "@/providers/i18n-provider";
import { useUserPreferences } from "@/providers/user-preferences-context";

interface AvatarSectionProps {
  userId: Accessor<string | null>;
  name: Accessor<string | null | undefined>;
  email: Accessor<string>;
  /**
   * Photo that comes from the SESSION. It is only the starting value: the local row
   * wins (see `image()` below), because the session comes from the API and is cached.
   */
  image: Accessor<string | null | undefined>;
}

export function AvatarSection(props: AvatarSectionProps) {
  const { t } = useI18n();
  const drizzle = useDrizzle();
  const { preferences } = useUserPreferences();

  /**
   * The EFFECTIVE picture.
   *
   * The local row takes precedence over the session: that is what makes the newly
   * uploaded picture appear immediately (the session is not refreshed on every write)
   * and remain after a cold reload without a connection. `undefined` = the row
   * has not loaded yet, so the session value applies; `null` = the row says there
   * is NO picture, and that wins (the "remove picture" case).
   */
  const image = () => {
    const local = preferences()?.image;
    return local === undefined ? props.image() : local;
  };
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);
  let inputRef: HTMLInputElement | undefined;

  /** Writes the key (or `null`) to the local row. */
  const writeLocal = async (key: string | null) => {
    const db = drizzle();
    const userId = props.userId();
    if (db && userId) {
      await updateUserImageMutation(db, userId, key);
    }
  };

  const handleFile = async (file: File) => {
    setError(null);

    const invalid = validateAvatarFile(file);
    if (invalid === "too-large") {
      setError(t(msg`That image is too large. Pick one under 5 MB.`));
      return;
    }
    if (invalid === "unsupported-type") {
      setError(t(msg`That file isn't an image we can use. Try a JPG, PNG or WebP.`));
      return;
    }

    setBusy(true);
    try {
      const prepared = await prepareAvatarFile(file);
      const { key } = await uploadAvatar(prepared);
      await writeLocal(key);
    } catch (e) {
      // `cause` carries the HTTP status (see `uploadAvatar`).
      const status = e instanceof Error ? e.cause : undefined;
      if (status === 403) {
        // Rule zero: this account cannot have a visible picture. It is explained without
        // blaming whoever is trying.
        setError(t(msg`This account can't set a profile picture.`));
      } else if (status === 429) {
        setError(t(msg`Too many changes in a row. Try again in a little while.`));
      } else if (status === 400) {
        setError(t(msg`We couldn't read that image. Try a different one.`));
      } else {
        setError(t(msg`We couldn't upload your picture. Check your connection and try again.`));
      }
    } finally {
      setBusy(false);
      // Allows choosing THE SAME file again after a failure: without this the
      // input does not fire `change` the second time.
      if (inputRef) {
        inputRef.value = "";
      }
    }
  };

  const handleRemove = async () => {
    setError(null);
    setBusy(true);
    try {
      await deleteAvatar();
      await writeLocal(null);
    } catch {
      setError(t(msg`We couldn't remove your picture. Check your connection and try again.`));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      class="flex flex-col items-center gap-4 sm:flex-row sm:items-center"
      data-testid="avatar-section"
    >
      <Avatar
        userId={props.userId()}
        name={props.name()}
        email={props.email()}
        image={image()}
        size="xl"
        alt={t(msg`Your profile picture`)}
        testId="avatar-section-preview"
      />

      <div class="flex flex-col items-center gap-2 sm:items-start">
        <div class="flex flex-wrap items-center justify-center gap-2">
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            class="sr-only"
            data-testid="avatar-file-input"
            onChange={(event) => {
              const file = event.currentTarget.files?.[0];
              if (file) {
                void handleFile(file);
              }
            }}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={busy()}
            onClick={() => inputRef?.click()}
            data-testid="avatar-change-button"
          >
            <Show when={busy()} fallback={<Camera class="mr-2 h-4 w-4" />}>
              <Loader2 class="mr-2 h-4 w-4 animate-spin" />
            </Show>
            {image() ? t(msg`Change photo`) : t(msg`Add photo`)}
          </Button>

          <Show when={image()}>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={busy()}
              onClick={() => void handleRemove()}
              data-testid="avatar-remove-button"
            >
              <Trash2 class="mr-2 h-4 w-4" />
              {t(msg`Remove`)}
            </Button>
          </Show>
        </div>

        <p class="text-xs text-muted-foreground">
          {t(msg`JPG, PNG or WebP, up to 5 MB. We crop it to a square.`)}
        </p>

        <Show when={error()}>
          {(message) => (
            <p class="text-xs text-destructive" role="alert" data-testid="avatar-error">
              {message()}
            </p>
          )}
        </Show>
      </div>
    </div>
  );
}
